import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FilterSelector } from './FilterSelector';
import { filterIds, producerOptions, tabs, terms } from '../../constants/suggestedAgents';
import { searchfilterStateId as sfState } from '../../constants/suggestedAgents';
import { useCommonStore, useSearchStore } from '../../../store/store';
import { IncDecProducers } from './producers/IncDecProducers';
import { NewProducers } from './producers/NewProducers';
import { getCurrentTabTimePeriod, getOptionValue, getValidationSettings } from './helpers/helpers';
import { views } from '../../constants/savedAgents';
import { searchTerms } from '../../../constants';

export const Producers = ({ setState, suggestedAgentsState }) => {
    const mlsProviders = useCommonStore(state => state.mlsProviders);
    const {
        currentInactivityPeriod,
        currentTabId,
        currentTabTimePeriod,
        minGrowth,
        maxGrowth,
        selectedLowPos,
        selectedMlsId,
        selectedTimeFrame,
        selectedNewProd,
        selectedInactivity,
        selectedTimePeriod,
        isGrowthAnalysisView,
    } = useSearchStore(state => {
        const { [state.selectedModule]: moduleState } = state;
        const { [moduleState.currentTabId]: tabState } = moduleState.searchOptions;
        const { producers } = tabState;
        const currentInactivityPeriod = producers?.inactivity?.label || null;
        const selectedListingViewId = tabState?.display.selectedView.value;
        const isGrowthAnalysisView = selectedListingViewId === views.growthAnalysis;
        const currentTabTimePeriod = getCurrentTabTimePeriod(producers, isGrowthAnalysisView);
        const selectedTimeFrame = isGrowthAnalysisView
            ? producers?.growthTimeFrame
            : producers?.timeFrame;
        return {
            currentInactivityPeriod,
            currentTabId: moduleState.currentTabId,
            currentTabTimePeriod,
            minGrowth: tabState.producers?.minGrowth,
            maxGrowth: tabState.producers?.maxGrowth,
            selectedLowPos: tabState.producers?.lowPos,
            selectedTimeFrame,
            selectedMlsId: tabState?.producers?.mls?.mlsId || moduleState?.selectedMls?.mlsId,
            selectedNewProd: tabState.producers?.newProd,
            selectedInactivity: tabState.producers?.inactivity,
            selectedTimePeriod: tabState.producers?.timePeriod || tabState.producers?.newProd || tabState.producers?.timeFrame,
            isGrowthAnalysisView,
        };
    });

    const isTopProducersTab = currentTabId === tabs.topProducers.id;
    const isIncOrDecProducersTab = currentTabId === tabs.incProducers.id || currentTabId === tabs.decProducers.id;

    useEffect(() => {
        if (isIncOrDecProducersTab) {
            setState(state => ({
                ...state,
                selectedFilterValues: {
                    ...state.selectedFilterValues,
                    [currentTabId]: {
                        ...state.selectedFilterValues?.[currentTabId],
                        minGrowth: minGrowth,
                        maxGrowth: maxGrowth,
                    },
                    filterId: filterIds.producers,
                }
            }));
        }
    }, [currentTabId]);

    const handleFilterChange = (option, filterId, stateId) => {
        const optionValue = getOptionValue(option, stateId);
        setState(state => ({
            ...state,
            selectedFilterValues: {
                ...state.selectedFilterValues,
                [currentTabId]: {
                    ...state.selectedFilterValues?.[currentTabId],
                    [stateId]: optionValue,
                },
                filterId,
                stateId,
            }
        }));
    };

    const selectedFilterValues = suggestedAgentsState?.selectedFilterValues[currentTabId];
    const validationSettings = getValidationSettings({
        minGrowth: selectedFilterValues?.minGrowth ?? minGrowth,
        maxGrowth: selectedFilterValues?.maxGrowth ?? maxGrowth,
        currentTabId
    });
    const mlsOptions = mlsProviders.map(mls => ({value: mls.mlsId, label: mls.shortDescription}));
    const incDecTimeFrame = isGrowthAnalysisView ? sfState.growthTimeFrame : sfState.timeFrame;
    const filterByTab = {
        [tabs.topProducers.id]: (
            <>
                <FilterSelector
                    containerClass='mb-3'
                    dataLwtId='select-producers-mls'
                    defaultValue={mlsOptions.find(mls => mls.value === selectedMlsId)}
                    labelText={searchTerms.mls}
                    options={mlsOptions}
                    onChange={(option) => handleFilterChange(option, filterIds.producers, sfState.mls)}
                    value={mlsOptions.find(mls => mls.value === (selectedFilterValues?.mls?.value ?? selectedMlsId))}
                />
                <FilterSelector
                    dataLwtId='select-producers-lowest-position'
                    defaultValue={selectedLowPos}
                    labelText={terms.lowestPosition}
                    options={producerOptions.lowPos}
                    onChange={(option) => handleFilterChange(option, filterIds.producers, sfState.lowPos)}
                    value={selectedFilterValues?.lowPos ?? selectedLowPos?.lowPos}
                />
            </>
        ),
        [tabs.incProducers.id]: (
            <IncDecProducers
                currentTabId={currentTabId}
                minLabel={terms.minIncrease}
                maxLabel={terms.maxIncrease}
                minDefault={minGrowth}
                maxDefault={maxGrowth}
                minGrowth={selectedFilterValues?.minGrowth ?? minGrowth}
                maxGrowth={selectedFilterValues?.maxGrowth ?? maxGrowth}
                onMinInputChange={(value) => handleFilterChange(value, filterIds.producers, sfState.minGrowth)}
                onMaxInputChange={(value) => handleFilterChange(value, filterIds.producers, sfState.maxGrowth)}
                onSelectorChange={(option) => handleFilterChange(option, filterIds.producers, incDecTimeFrame)}
                selectorDefault={selectedTimeFrame}
                validationMessage={validationSettings?.validationMessage}
                isGrowthAnalysisView={isGrowthAnalysisView}
                handleFilterChange={handleFilterChange}
                mlsOptions={mlsOptions}
                selectedMlsId={selectedMlsId}
                selectedFilterValues={selectedFilterValues}
            />
        ),
        [tabs.decProducers.id]: (
            <IncDecProducers
                currentTabId={currentTabId}
                minLabel={terms.minDecrease}
                maxLabel={terms.maxDecrease}
                minDefault={minGrowth}
                maxDefault={maxGrowth}
                minGrowth={selectedFilterValues?.minGrowth ?? minGrowth}
                maxGrowth={selectedFilterValues?.maxGrowth ?? maxGrowth}
                onMinInputChange={(value) => handleFilterChange(value, filterIds.producers, sfState.minGrowth)}
                onMaxInputChange={(value) => handleFilterChange(value, filterIds.producers, sfState.maxGrowth)}
                onSelectorChange={(option) => handleFilterChange(option, filterIds.producers, incDecTimeFrame)}
                selectorDefault={selectedTimeFrame}
                validationMessage={validationSettings?.validationMessage}
                isGrowthAnalysisView={isGrowthAnalysisView}
                handleFilterChange={handleFilterChange}
                mlsOptions={mlsOptions}
                selectedMlsId={selectedMlsId}
                selectedFilterValues={selectedFilterValues}
            />
        ),
        [tabs.newProducers.id]: (
            <NewProducers
                inactivityDefault={selectedInactivity}
                newProdDefault={selectedNewProd}
                onInactivityChange={(option) => handleFilterChange(option, filterIds.producers, sfState.inactivity)}
                onNewProdChange={(option) => handleFilterChange(option, filterIds.producers, sfState.newProd)}
                value={suggestedAgentsState?.selectedFilterValues[currentTabId] || {currentTabTimePeriod, currentInactivityPeriod}}
                mlsOptions={mlsOptions}
                selectedMlsId={selectedMlsId}
                selectedFilterValues={selectedFilterValues}
                handleFilterChange={handleFilterChange}
            />
        ),
        [tabs.coopAgents.id]: (
            <>
                <FilterSelector
                    containerClass='mb-3'
                    dataLwtId='select-producers-mls'
                    defaultValue={mlsOptions.find(mls => mls.value === selectedMlsId)}
                    labelText={searchTerms.mls}
                    options={mlsOptions}
                    onChange={(option) => handleFilterChange(option, filterIds.producers, sfState.mls)}
                    value={mlsOptions.find(mls => mls.value === (selectedFilterValues?.mls?.value ?? selectedMlsId))}
                />
                <FilterSelector
                    dataLwtId='select-producers-time-period'
                    defaultValue={selectedTimePeriod}
                    labelText={terms.timePeriod}
                    options={producerOptions.timePeriod}
                    onChange={(option) => handleFilterChange(option, filterIds.producers, sfState.timePeriod)}
                    value={suggestedAgentsState?.selectedFilterValues[currentTabId]?.timePeriod || currentTabTimePeriod}
                />
            </>
        ),
    };

    return (
        <div className='producer-filter py-2'dataLwtId='suggested-agents-producers-filter'>
            <div className={`${isTopProducersTab ? 'pb-1' : 'pb-3'}`}>{filterByTab[currentTabId]}</div>
        </div>
    );
};

Producers.propTypes = {
    filterSettings: PropTypes.object,
    setFilterSettings: PropTypes.func,
    setState: PropTypes.func,
    togglePopover: PropTypes.func,
    suggestedAgentsState: PropTypes.object,
};
